import React, { memo, useCallback, useEffect, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/captions.css';

import bn from '../../generalUtils/benames';
import { IMAGE, VIDEO } from '../../constants/messageConst';
import { YOUTUBE_BILLBOARD } from '../../constants/commonConst';
import { svgVideoIcon } from '../../constants/svgConst';

const bemClass = bn.create('reactLightBox');

/* PROPERTIES:
slides : {
   src: PATH HERE,
   alt: NAME HERE,
   type: 'image' or 'video'
   customControls: ( // Optional
     // JSX
   )
,
open : bool,
close : function,
startIndex : selected image or video index
*/

const ReactLightBox = props => {
  const { open, startIndex = 1, slides, close } = props;
  const DEFAULT_PLUGIN = [Captions, Fullscreen, Slideshow, Thumbnails, Zoom, Counter, Video];

  const [index, setIndex] = useState(startIndex);
  const [firstInitialized, setFirstInitialized] = useState(false);
  const [plugins, setPlugins] = useState(DEFAULT_PLUGIN);

  const slidesArr = slides.map(url => {
    const type = url?.type || IMAGE;
    const src = url?.src || '';
    const thumb_path = url?.thumb_path || '';
    const alt = url?.alt || '';
    if (type === IMAGE || type === YOUTUBE_BILLBOARD) {
      return {
        src,
        type,
        alt,
        thumb_path,
        description: alt ? alt : type
      };
    } else if (type === VIDEO) {
      return {
        customControls: url?.customControls || null,
        type,
        thumb_path,
        description: alt ? alt : type,
        sources: [{ src, type: 'video/mp4' }]
      };
    }
  });

  const generateToolbarButtons = useCallback(
    index => {
      const currentUrl = slides[index || 0];
      const buttons = [currentUrl?.customControls, 'close'];
      return buttons.filter(button => button !== undefined);
    },
    [index, slides]
  );

  useEffect(() => {
    if (!firstInitialized) {
      setFirstInitialized(true);
    }
  }, [startIndex]);

  useEffect(() => {
    const currentUrl = slides[index || 0];
    if (currentUrl?.type === VIDEO) {
      setPlugins([Captions, Fullscreen, Thumbnails, Counter, Video]);
    } else {
      setPlugins(DEFAULT_PLUGIN);
    }
  }, [index, slides]);

  return (
    <>
      <Lightbox
        on={{
          view: ({ index: currentIndex }) => {
            if (firstInitialized) {
              setIndex(startIndex);
              setFirstInitialized(false);
            } else {
              setIndex(currentIndex);
            }
          }
        }}
        plugins={plugins}
        open={open}
        close={() => {
          close();
          document.body.style = '';
        }}
        index={index}
        counter={{ container: { style: { top: 0, bottom: 'unset', fontSize: '1rem', fontWeight: 'bold' } } }}
        captions={{ descriptionTextAlign: 'center' }}
        slides={slidesArr}
        className={bemClass.b('')}
        carousel={{
          finite: true,
          imageFit: 'contain',
          preload: 3
        }}
        render={{
          buttonPrev: slides.length <= 1 ? () => null : undefined,
          buttonNext: slides.length <= 1 ? () => null : undefined,

          slide: ({ slide }) =>
            slide.type === YOUTUBE_BILLBOARD ? (
              <iframe
                src={slide.src}
                title="YouTube Billboard"
                style={{ width: '70%', height: '100%' }}
                frameBorder="0"
                allowFullScreen
              />
            ) : undefined,

          thumbnail: ({ slide }) => {
            return (
              <div style={{ position: 'relative' }}>
                {slide.type === VIDEO ? (
                  <video width="100%" alt="video">
                    Your browser does not support the video tag.
                    <source src={slide?.thumb_path || ''} type="video/mp4" />
                  </video>
                ) : (
                  <img src={slide.thumb_path} style={{ width: '100%' }} />
                )}

                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '24px',
                    color: 'white'
                  }}
                >
                  {slide.type === VIDEO && svgVideoIcon('', '#ffffff', 30)}
                  {slide.type === YOUTUBE_BILLBOARD && (
                    <i className="fa fa-youtube-play" style={{ color: 'red' }} />
                  )}
                </div>
              </div>
            );
          }
        }}
        toolbar={{
          buttons: generateToolbarButtons(index)
        }}
        video={{
          controls: true,
          playsInline: true
        }}
      />
    </>
  );
};

ReactLightBox.displayName = 'ReactLightBox';
export default memo(ReactLightBox);
